<template>
  <div class="app-container">
    <!-- 顶部导航栏 -->
    <el-header class="app-header">
      <div class="header-left">
        <el-button type="text" @click="toggleSidebar" class="sidebar-toggle">
          <i class="el-icon-menu"></i>
        </el-button>
        <h2>小鱼智能控制台</h2>
      </div>
      <div class="header-right">
        <el-button type="text" @click="goToUserDashboard">
          <i class="el-icon-s-home"></i>
          <span class="logout-text">机器人控制台</span>
        </el-button>
        <el-button type="text" @click="handleLogout">
          <i class="el-icon-switch-button"></i>
          <span class="logout-text">退出登录</span>
        </el-button>
      </div>
    </el-header>

    <!-- 主体内容 -->
    <el-container class="main-container">
      <!-- 左侧菜单 -->
      <el-aside :width="sidebarWidth" class="app-aside" :class="{ 'sidebar-collapsed': isCollapsed }">
        <el-menu :default-active="activeMenu" class="app-menu" :collapse="isCollapsed" @select="handleMenuSelect">
          <el-menu-item 
            v-for="item in menuItems" 
            :key="item.index" 
            :index="item.index"
          >
            <i :class="item.icon"></i>
            <span>{{ item.title }}</span>
          </el-menu-item>
        </el-menu>
      </el-aside>

      <!-- 右侧内容区 -->
      <el-main class="app-main" :class="{ 'main-expanded': isCollapsed }">
        <router-view />
      </el-main>
    </el-container>

    <!-- 移动端遮罩 -->
    <div v-if="isMobile && !isCollapsed" class="mobile-overlay" @click="closeSidebar"></div>
  </div>
</template>

<script>
import { removeCookie } from '@/utils/cookie'
import { logout } from '@/api/login'
import { getMenuConfig, generateMenuFromRoutes } from '@/config/menu'

export default {
  name: "MainLayoutPage",
  data() {
    return {
      isCollapsed: false,
      isMobile: false,
      // 菜单配置将在mounted中初始化
      menuConfig: []
    };
  },
  computed: {
    // 菜单项列表
    menuItems() {
      return this.menuConfig;
    },
    
    // 根据当前路由动态设置激活的菜单项
    activeMenu() {
      const currentPath = this.$route.path;
      
      // 如果菜单配置还没有加载，返回默认值
      if (!this.menuConfig || this.menuConfig.length === 0) {
        return 'admin/dashboard';
      }
      
      // 查找匹配的菜单项
      const matchedItem = this.findMatchingMenuItem(currentPath);
      
      if (matchedItem) {
        return matchedItem.index;
      }
      
      // 如果没有找到匹配项，返回默认菜单项
      const defaultItem = this.menuConfig.find(item => item.isDefault);
      if (defaultItem) {
        return defaultItem.index;
      }
      
      // 如果都没有找到，返回第一个菜单项
      return this.menuConfig[0] ? this.menuConfig[0].index : 'admin/dashboard';
    },
    
    sidebarWidth() {
      return this.isCollapsed ? "64px" : "200px";
    },
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    // 初始化菜单配置
    this.menuConfig = this.getMenuConfiguration();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  watch: {
    // 监听路由变化，确保菜单状态正确更新
    '$route'() {
      // 路由变化时，计算属性会自动重新计算，菜单状态会自动更新
    }
  },
  methods: {
    /**
     * 获取菜单配置
     * 支持两种方式：配置文件或路由自动生成
     */
    getMenuConfiguration() {
      // 方式1：从路由配置自动生成菜单（推荐）
      const adminRoutes = this.$router.options.routes.find(route => route.path === '/admin');
      if (adminRoutes && adminRoutes.children) {
        const menuFromRoutes = generateMenuFromRoutes(adminRoutes.children);
        if (menuFromRoutes.length > 0) {
          return menuFromRoutes;
        }
      }
      
      // 方式2：从配置文件获取（备用方案）
      return getMenuConfig('admin');
    },

    /**
     * 智能路由匹配算法
     * 支持精确匹配、前缀匹配和子路径匹配
     */
    findMatchingMenuItem(currentPath) {
      // 如果菜单配置为空，返回null
      if (!this.menuConfig || this.menuConfig.length === 0) {
        return null;
      }
      
      // 1. 精确匹配
      let matchedItem = this.menuConfig.find(item => item.path === currentPath);
      if (matchedItem) {
        return matchedItem;
      }
      
      // 2. 前缀匹配（支持子路径）
      // 按路径长度排序，优先匹配更长的路径
      const sortedItems = this.menuConfig
        .filter(item => item.path !== '/admin') // 排除根路径
        .sort((a, b) => b.path.length - a.path.length);
      
      matchedItem = sortedItems.find(item => {
        return currentPath.startsWith(item.path + '/') || 
               currentPath.startsWith(item.path);
      });
      if (matchedItem) {
        return matchedItem;
      }
      
      // 3. 处理根路径重定向
      if (currentPath === '/admin' || currentPath === '/admin/') {
        const defaultItem = this.menuConfig.find(item => item.isDefault);
        return defaultItem;
      }
      
      return null;
    },

    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        this.isCollapsed = true;
      }
    },

    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },

    closeSidebar() {
      if (this.isMobile) {
        this.isCollapsed = true;
      }
    },

    handleMenuSelect(index) {
      // 根据菜单配置找到对应的路径
      const menuItem = this.menuConfig.find(item => item.index === index);
      if (menuItem && this.$route.path !== menuItem.path) {
        this.$router.push(menuItem.path).catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
      }
      // 移动端选择菜单后自动收起侧边栏
      if (this.isMobile) {
        this.isCollapsed = true;
      }
    },

    goToUserDashboard() {
      this.$router.push('/');
    },

    handleLogout() {
      // 清除cookie并跳转到登录页
      logout().then(() => {
        removeCookie('token')
        removeCookie('userInfo')
        removeCookie('session')
        this.$message.info("已退出登录");
        this.$router.push("/login");
      }).catch(() => {
      })
    },
  },
};
</script>

<style scoped>
.app-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.sidebar-toggle {
  font-size: 18px;
  color: #666;
}

.header-left h2 {
  color: #409eff;
  margin: 0;
}

.header-right .el-button {
  color: #666;
}

.main-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.app-aside {
  background: #fff;
  border-right: 1px solid #e6e6e6;
  transition: width 0.3s ease;
  z-index: 999;
}

.app-menu {
  border-right: none;
  height: 100%;
}

.app-main {
  padding: 20px;
  background: #f5f5f5;
  overflow-y: auto;
  transition: margin-left 0.3s ease;
}

.main-expanded {
  margin-left: 0;
}

.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .app-header {
    padding: 0 15px;
  }

  .header-left h2 {
    font-size: 18px;
  }

  .logout-text {
    display: none;
  }

  .app-main {
    padding: 15px;
  }

  .app-aside {
    position: fixed;
    left: 0;
    top: 60px;
    height: calc(100vh - 60px);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .app-aside:not(.sidebar-collapsed) {
    transform: translateX(0);
  }
}

@media (max-width: 480px) {
  .app-header {
    padding: 0 10px;
  }

  .header-left h2 {
    font-size: 16px;
  }

  .app-main {
    padding: 10px;
  }
}
</style>
